import React from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

/**
 * Props for the TelematicsErrorPanel component.
 */
interface TelematicsErrorPanelProps {
	/** Error title text. Default: "Something went wrong" */
	title?: string;
	/** Detailed error message. Default: "We couldn't load your trip details..." */
	message?: string;
	/** Callback fired when retry button is clicked. If omitted, retry button is hidden. */
	onRetry?: () => void;
	/** Callback fired when back button is clicked. If omitted, back button is hidden. */
	onBack?: () => void;
	/** Label for the retry button. Default: "Try Again" */
	retryLabel?: string;
	/** Label for the back button. Default: "Go Back" */
	backLabel?: string;
}

/**
 * Displays a user-friendly error state with optional retry and back actions.
 *
 * Used throughout the telematics feature to provide consistent error handling
 * UI when API calls fail or data cannot be loaded. Features a centered layout
 * with an error icon, customizable title and message, and optional action buttons.
 *
 * @component
 * @example
 * ```tsx
 * // Basic usage with retry action
 * <TelematicsErrorPanel
 *   title="Failed to load trips"
 *   message="Please check your connection and try again."
 *   onRetry={() => dispatch(fetchTrips())}
 * />
 *
 * // With both retry and back navigation
 * <TelematicsErrorPanel
 *   onRetry={handleRetry}
 *   onBack={() => navigate(-1)}
 * />
 * ```
 */
const TelematicsErrorPanel: React.FC<TelematicsErrorPanelProps> = ({
	title = 'Something went wrong',
	message = "We couldn't load your trip details at the moment. Please try again or come back later.",
	onRetry,
	onBack,
	retryLabel = 'Try Again',
	backLabel = 'Go Back',
}) => {
	const theme = useTheme();

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				justifyContent: 'center',
				py: 8,
				px: 3,
				textAlign: 'center',
			}}
		>
			{/* Error Icon */}
			<Box
				sx={{
					width: 80,
					height: 80,
					borderRadius: '50%',
					backgroundColor: `${theme.palette.error.main}15`,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					mb: 3,
				}}
			>
				<ErrorOutlineIcon
					sx={{
						fontSize: 40,
						color: theme.palette.error.main,
					}}
				/>
			</Box>

			{/* Title and message, announced together */}
			<Box role='alert' sx={{ display: 'contents' }}>
				<Typography
					variant='h6'
					sx={{
						fontWeight: 600,
						color: theme.palette.text.primary,
						mb: 1.5,
					}}
				>
					{title}
				</Typography>

				<Typography
					variant='body1'
					sx={{
						color: theme.palette.text.secondary,
						maxWidth: 400,
						mb: 4,
						lineHeight: 1.6,
					}}
				>
					{message}
				</Typography>
			</Box>

			{/* Action Buttons */}
			<Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
				{onRetry && (
					<Button
						variant='contained'
						color='primary'
						startIcon={<RefreshIcon />}
						onClick={onRetry}
						sx={{
							textTransform: 'none',
							px: 3,
							py: 1,
						}}
					>
						{retryLabel}
					</Button>
				)}
				{onBack && (
					<Button
						variant='outlined'
						color='primary'
						startIcon={<ArrowBackIcon />}
						onClick={onBack}
						sx={{
							textTransform: 'none',
							px: 3,
							py: 1,
						}}
					>
						{backLabel}
					</Button>
				)}
			</Box>
		</Box>
	);
};

export default TelematicsErrorPanel;
